import React from "react";
import { ArrowUpRight, Lightbulb, CalendarPlus, Sparkles, FileText, Plus, MessageCircle } from "lucide-react";
import { DarkPanel, HATCH, SectionCard } from "./StatusBadge";
import { weekStart, addDays, inWeek, toIso, DAYS, fmtDay, sumMetrics, engagement, fmtPct, fmtNum, parseIso } from "./contentUtils";

function WeekBoard({ items, onOpen, actions }) {
  const ws = weekStart();
  const week = items.filter((i) => inWeek(i.date, ws));
  const done = week.filter((i) => i.status === "publie").length;
  const left = week.length - done;
  const today = toIso(new Date());
  const add = async (iso) => { const it = await actions.createItem({ date: iso, status: "planifie" }); if (it) onOpen(it.id); };
  return (
    <DarkPanel data-testid="overview-week">
      <div className="grid gap-6 lg:grid-cols-[230px_1fr]">
        <div className="flex flex-col">
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/50">Ta semaine</p>
          <p data-testid="overview-progress-label" className="mt-3 font-sans text-5xl font-medium">{done}<span className="text-2xl text-white/40"> / {week.length}</span></p>
          <p className="text-sm text-white/60">posts publiés</p>
          <div className="mt-auto pt-6">
            <div className="h-2.5 overflow-hidden rounded-full" style={HATCH}><div data-testid="overview-progress-bar" className="h-full rounded-full bg-bordeaux transition-[width] duration-700" style={{ width: `${week.length ? (done / week.length) * 100 : 0}%`, boxShadow: "0 0 18px rgba(123,17,58,0.9)" }} /></div>
            <p className="mt-2 text-[11px] text-white/50">{week.length === 0 ? "Aucun post prévu cette semaine" : left > 0 ? `Encore ${left} post${left > 1 ? "s" : ""} à publier cette semaine` : "Semaine bouclée, bravo ✨"}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="grid min-w-[620px] grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, n) => {
              const d = addDays(ws, n);
              const iso = toIso(d);
              const list = week.filter((i) => i.date === iso);
              return (
                <div key={iso} data-testid={`overview-day-${iso}`} className={`flex min-h-[150px] flex-col rounded-2xl border p-2 ${iso === today ? "border-bordeaux bg-bordeaux/10" : "border-white/10 bg-white/[0.03]"}`}>
                  <p className="mb-2 text-[11px] text-white/50">{DAYS[n]} {d.getDate()}</p>
                  <div className="space-y-1">{list.map((i) => (
                    <button key={i.id} data-testid={`overview-item-${i.id}`} onClick={() => onOpen(i.id)} className="flex w-full items-center gap-1.5 rounded-lg px-1 py-0.5 text-left text-[11px] text-white/85 hover:bg-white/10">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${i.status === "publie" ? "bg-bordeaux ring-2 ring-[#F3D2D9]/40" : "bg-white/40"}`} /><span className="truncate">{i.title || "Sans titre"}</span>
                    </button>
                  ))}</div>
                  {list.length === 0 && <button data-testid={`overview-add-${iso}`} onClick={() => add(iso)} className="m-auto rounded-full p-1.5 text-white/30 transition-colors hover:bg-white/10 hover:text-white"><Plus size={16} /></button>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DarkPanel>
  );
}

function Tile({ icon: Icon, label, count, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex min-h-[120px] w-full items-center gap-4 rounded-[28px] bg-bordeaux p-6 text-left text-white transition-transform hover:-translate-y-0.5">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15"><Icon size={18} /></span>
      <span className="flex-1"><span className="block font-medium">{label}</span><span className="text-xs text-white/70">{count} élément(s)</span></span>
      <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-white px-2 text-xs font-semibold text-bordeaux">{count}</span>
    </button>
  );
}

function Upcoming({ items, onOpen, onTab }) {
  const today = toIso(new Date());
  const next = items.filter((i) => i.date && i.date >= today && i.status !== "publie").sort((a, b) => a.date.localeCompare(b.date)).slice(0, 4);
  return (
    <SectionCard data-testid="overview-upcoming" className="flex flex-col">
      <div className="flex items-center justify-between"><p className="font-medium text-ink">À venir</p><button onClick={() => onTab("calendrier")} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-muted hover:text-bordeaux"><ArrowUpRight size={14} /></button></div>
      {next.length === 0 ? (
        <div className="m-auto py-6 text-center"><p className="text-sm text-ink-muted">Rien de prévu</p><button onClick={() => onTab("idees", "mine")} className="mt-3 rounded-full bg-[#2D181E] px-5 py-2 text-xs font-medium text-white">Planifier une idée</button></div>
      ) : (
        <div className="mt-4 space-y-2">{next.map((i) => (
          <button key={i.id} onClick={() => onOpen(i.id)} className="flex w-full items-center gap-3 rounded-2xl bg-cream px-3 py-2.5 text-left hover:bg-bordeaux-tint">
            <span className="w-14 shrink-0 text-[11px] font-medium uppercase text-bordeaux">{DAYS[(parseIso(i.date).getDay() + 6) % 7]} {parseIso(i.date).getDate()}</span>
            <span className="flex-1 truncate text-sm text-ink">{i.title || "Sans titre"}</span>
          </button>
        ))}</div>
      )}
    </SectionCard>
  );
}

function LastReport({ report, items }) {
  if (!report) return null;
  const ws = parseIso(report.week_start);
  const t = sumMetrics(items.filter((i) => inWeek(i.date, ws) && i.status === "publie"));
  return (
    <SectionCard data-testid="overview-last-report">
      <p className="eyebrow">Dernier bilan · semaine du {fmtDay(report.week_start)}</p>
      <div className="mt-3 flex gap-8"><div><p className="font-sans text-2xl font-medium text-ink">{fmtNum(t.views)}</p><p className="text-xs text-ink-muted">vues</p></div><div><p className="font-sans text-2xl font-medium text-ink">{fmtPct(engagement(t))}</p><p className="text-xs text-ink-muted">engagement</p></div></div>
      {report.feedback ? <p className="mt-4 flex gap-2 rounded-2xl bg-bordeaux-tint/60 p-3 text-sm font-light text-ink-soft"><MessageCircle size={15} className="mt-0.5 shrink-0 text-bordeaux" /><span className="line-clamp-3">{report.feedback}</span></p> : <p className="mt-3 text-xs italic text-ink-muted">En attente du retour de ta coach…</p>}
    </SectionCard>
  );
}

export default function ContentOverview({ items, reports, inspirations, onOpen, onTab, actions }) {
  const ws = weekStart();
  const week = items.filter((i) => inWeek(i.date, ws));
  const freeDays = 7 - new Set(week.map((i) => i.date)).size;
  const ideas = items.filter((i) => !i.date);
  const nextScript = items.filter((i) => i.date && i.status !== "publie").sort((a, b) => a.date.localeCompare(b.date))[0];
  const quick = [[Lightbulb, "Idée", () => onTab("idees", "mine")], [CalendarPlus, "Planifier", () => onTab("calendrier")], [Sparkles, "Inspi", () => onTab("idees", "insp")], [FileText, "Script", () => (nextScript ? onOpen(nextScript.id) : onTab("calendrier"))]];
  return (
    <div className="space-y-5">
      <WeekBoard items={items} onOpen={onOpen} actions={actions} />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <DarkPanel className="flex flex-col">
          <div className="flex items-start justify-between"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-bordeaux"><ArrowUpRight size={16} /></span></div>
          <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.2em] text-white/50">{freeDays > 0 ? "Il te reste des jours libres" : "Semaine remplie"}</p>
          <p className="mt-1 font-sans text-xl font-medium">{freeDays} jour{freeDays > 1 ? "s" : ""} sans post cette semaine</p>
          <button data-testid="overview-plan" onClick={() => onTab("idees", "mine")} className="mt-4 w-fit rounded-full bg-bordeaux px-5 py-2 text-xs font-medium">Planifier une idée</button>
          <div className="mt-6 grid grid-cols-4 gap-2">{quick.map(([I, l, fn]) => (
            <button key={l} data-testid={`overview-quick-${l}`} onClick={fn} className="flex flex-col items-center gap-1.5 text-[11px] text-white/60 hover:text-white"><span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5"><I size={16} /></span>{l}</button>
          ))}</div>
        </DarkPanel>
        <div className="space-y-5"><Upcoming items={items} onOpen={onOpen} onTab={onTab} /><LastReport report={reports[0]} items={items} /></div>
        <div className="space-y-5">
          <Tile testid="overview-shortcut-insp" icon={Sparkles} label="Inspirations" count={inspirations.length} onClick={() => onTab("idees", "insp")} />
          <Tile testid="overview-shortcut-ideas" icon={Lightbulb} label="Idées" count={ideas.filter((i) => i.source !== "coach").length} onClick={() => onTab("idees", "mine")} />
          <Tile testid="overview-shortcut-coach" icon={MessageCircle} label="Idées de ma coach" count={ideas.filter((i) => i.source === "coach").length} onClick={() => onTab("idees", "coach")} />
        </div>
      </div>
    </div>
  );
}
